<template>
    <div class="shareDocument_wrapper" :class="{ '--auth-required': isProtectedPage && !isAuthenticated }">
        <div class="shareDocument_container">
            <div class="shareDocument_card">
                <div class="shareDocument_header">
                    <div class="shareDocument_status-fixed">
                        <div class="shareDocument_status-container">
                            <div class="shareDocument_status-indicator"></div>
                            <span class="shareDocument_status-badge">Online</span>
                        </div>
                    </div>
                    
                    <div class="shareDocument_header-container">
                        <div class="shareDocument_logo">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/8/85/Logo-Test.png" alt="Logo da empresa" class="shareDocument_logo-image" />
                        </div>
                        
                        <div class="shareDocument_content-header">
                            <h1 class="shareDocument_title">{{ pageTitle || 'Portal de Arquivos' }}</h1>
                            <p class="shareDocument_description">{{ pageDescription || 'Acesse e baixe seus arquivos com segurança e facilidade.' }}</p>
                        </div>
                    </div>
                </div>

                <div class="shareDocument_content">
                    <!-- Formulário de Login (para páginas protegidas) -->
                    <div v-if="isProtectedPage && !isAuthenticated" class="shareDocument_auth">
                        <div class="shareDocument_auth-header">
                            <div class="shareDocument_auth-icon">
                                <sw-icon name="shield" />
                            </div>
                            <div class="shareDocument_auth-text">
                                <h3 class="shareDocument_auth-title">Acesso Protegido</h3>
                                <p class="shareDocument_auth-description">Faça login para acessar os documentos</p>
                            </div>
                        </div>

                        <form @submit.prevent="handleAuthentication" class="shareDocument_auth-form">
                            <div class="shareDocument_auth-field">
                                <sw-input
                                    v-model="authCredentials.username"
                                    label="Usuário"
                                    icon="user"
                                    :validation="usernameValidation"
                                    @blur="validateUsername"
                                    placeholder="Digite seu usuário"
                                />
                            </div>

                            <div class="shareDocument_auth-field">
                                <sw-input
                                    v-model="authCredentials.password"
                                    label="Senha"
                                    type="password"
                                    icon="lock"
                                    :validation="passwordValidation"
                                    @blur="validatePassword"
                                    placeholder="Digite sua senha"
                                />
                            </div>

                            <div class="shareDocument_auth-error" v-if="authError">
                                <AlertMessage
                                    :type="ALERT_VARIANTS.ERROR"
                                    :description="authError"
                                />
                            </div>

                            <div class="shareDocument_auth-actions">
                                <sw-button
                                    name="Acessar Documentos"
                                    color="blue"
                                    icon="checkmark"
                                    type="submit"
                                    size="lg"
                                    :disabled="!isAuthFormValid || authenticating"
                                    :spin="authenticating"
                                />
                            </div>
                        </form>
                    </div>

                    <!-- Loading State para Documentos -->
                    <div v-else-if="loadingDocuments" class="shareDocument_loading">
                        <app-spinner :text="isLoadingAfterAuth ? 'Autenticando e carregando arquivos...' : 'Carregando arquivos...'" />
                    </div>

                    <!-- Conteúdo dos Documentos -->
                    <template v-else>
                        <!-- Indicador de Acesso Autenticado -->
                        <div v-if="isProtectedPage && isAuthenticated" class="shareDocument_auth-status">
                            <div class="shareDocument_auth-status-badge">
                                <sw-icon name="shield" class="shareDocument_auth-status-icon" />
                                <span class="shareDocument_auth-status-text">Acesso Autenticado</span>
                            </div>
                        </div>

                        <div class="shareDocument_summary">
                            <span class="shareDocument_summary-text">
                                {{ documents.length }} arquivo{{ documents.length !== 1 ? 's' : '' }} disponível{{ documents.length !== 1 ? 'eis' : '' }}
                            </span>
                            <sw-button
                                name="Baixar Todos"
                                color="blue"
                                icon="download"
                                size="sm"
                                @click="downloadAllDocuments"
                                :disabled="documents.length === 0"
                            />
                        </div>

                        <div class="shareDocument_documents" v-if="documents.length > 0">
                            <div class="shareDocument_document-item" v-for="doc in documents" :key="doc.id">
                                <div class="shareDocument_document-info">
                                    <div class="shareDocument_document-icon">
                                        <sw-icon :name="getDocumentIcon(doc.type)" class="shareDocument_document-iconSvg" />
                                    </div>
                                    <div class="shareDocument_document-details">
                                        <span class="shareDocument_document-name">{{ doc.name }}</span>
                                        <span class="shareDocument_document-meta">{{ doc.size }} • {{ doc.type }}</span>
                                    </div>
                                </div>
                                <sw-button
                                    icon="download"
                                    circle-icon
                                    color="blue"
                                    size="sm"
                                    @click="downloadIndividualDocument(doc)"
                                />
                            </div>
                        </div>

                        <!-- Estado vazio -->
                        <div v-else class="shareDocument_empty">
                            <div class="shareDocument_empty-content">
                                <sw-icon name="file-text" class="shareDocument_empty-icon" />
                                <h3 class="shareDocument_empty-title">Nenhum documento encontrado</h3>
                                <p class="shareDocument_empty-description">
                                    {{ isProtectedPage ? 'Esta página protegida não possui documentos disponíveis.' : 'Não há documentos públicos disponíveis no momento.' }}
                                </p>
                            </div>
                        </div>
                    </template>
                </div>
                
                <div class="shareDocument_footer">
                    <div class="shareDocument_security">
                        <div class="shareDocument_security-badges">
                            <div class="shareDocument_security-badge">
                                <sw-icon name="shield" class="shareDocument_security-icon" />
                                <span class="shareDocument_security-text">Segurança por SSL</span>
                            </div>

                            <a href="https://filesave.com.br" target="_blank" rel="noopener noreferrer" class="shareDocument_security-badge shareDocument_security-link">
                                <sw-icon name="shield" class="shareDocument_security-icon" />
                                <span class="shareDocument_security-text">Protegido por FileSave</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { notify, loadingFull } from 'sysweb-vue-components/src/global';
import { authenticatePublicPageUser, fetchPublicPageDocuments } from '@/services/mockApi';
import AlertMessage from '@Component/alerts/AlertMessage.vue';
import { ALERT_VARIANTS } from '@/constants';

const route = useRoute();
const pageTitle = ref('');
const pageDescription = ref('');

// Estados de autenticação
const isAuthenticated = ref(false);
const authenticating = ref(false);
const authError = ref('');
const isProtectedPage = ref(false);

// Estados de carregamento dos documentos
const loadingDocuments = ref(false);
const isLoadingAfterAuth = ref(false);

// Logo da empresa/usuário
const userCompanyLogo = ref('https://upload.wikimedia.org/wikipedia/commons/8/85/Logo-Test.png');

// Credenciais do formulário
const authCredentials = ref({
    username: '',
    password: ''
});

// Validações dos campos
const usernameValidation = ref({ isValid: true, error: { message: '' } });
const passwordValidation = ref({ isValid: true, error: { message: '' } });

// Computed para verificar se o formulário é válido
const isAuthFormValid = computed(() => {
    return authCredentials.value.username.length > 0 &&
           authCredentials.value.password.length > 0 &&
           usernameValidation.value.isValid &&
           passwordValidation.value.isValid;
});

// Documentos inicialmente vazios - serão carregados via API
const documents = ref([]);

function getDocumentIcon(fileType) {
    const iconMap = {
        'PDF': 'file-pdf',
        'DOCX': 'file-word',
        'XLSX': 'file-excel',
        'DOC': 'file-word',
        'XLS': 'file-excel'
    };
    return iconMap[fileType] || 'file-text';
}

// Funções de validação
function validateUsername() {
    const username = authCredentials.value.username;
    let isValid = true;
    let message = '';

    if (!username || username.length < 3) {
        isValid = false;
        message = 'Usuário deve ter pelo menos 3 caracteres.';
    }

    usernameValidation.value = { isValid, error: { message } };
}

function validatePassword() {
    const password = authCredentials.value.password;
    let isValid = true;
    let message = '';

    if (!password || password.length < 4) {
        isValid = false;
        message = 'Senha deve ter pelo menos 4 caracteres.';
    }

    passwordValidation.value = { isValid, error: { message } };
}

// Função auxiliar para chamar a API de documentos
async function fetchDocuments(authToken = null) {
    try {
        const response = await fetchPublicPageDocuments({
            isProtected: isProtectedPage.value,
            authToken
        });

        return response;

    } catch (error) {
        return {
            status: 'error',
            message: 'Erro ao carregar documentos'
        };
    }
}

// Função de autenticação
async function handleAuthentication() {
    // Validar campos antes de enviar
    validateUsername();
    validatePassword();

    if (!isAuthFormValid.value) {
        authError.value = 'Por favor, preencha todos os campos corretamente.';
        return;
    }

    try {
        authenticating.value = true;
        authError.value = '';

        // Chamar API de autenticação
        const response = await authenticatePublicPageUser(authCredentials.value);

        if (response.status === 'success') {
            // Salvar dados do usuário autenticado (poderia ser em store/localStorage)
            const userData = response.data;

            isAuthenticated.value = true;

            notify(`Acesso autorizado! Bem-vindo, ${userData.user.name}`, 'success');

            // Log para desenvolvimento
            console.log('✅ Usuário autenticado:', userData);

            // Marcar que o próximo loading é pós-autenticação
            isLoadingAfterAuth.value = true;

            // Carregar documentos protegidos após autenticação
            await loadDocuments(userData.token);

        } else {
            authError.value = response.message || 'Credenciais inválidas. Verifique seu usuário e senha.';
        }

    } catch (error) {
        console.error('❌ Erro na autenticação:', error);
        authError.value = 'Erro de conexão. Tente novamente.';
    } finally {
        authenticating.value = false;
    }
}

// Função para carregar documentos
async function loadDocuments(authToken = null) {
    try {
        loadingDocuments.value = true;

        const response = await fetchDocuments(authToken);

        if (response.status === 'success') {
            documents.value = response.data.documents;

            console.log('📁 Documentos carregados:', {
                total: response.data.total,
                accessLevel: response.data.accessLevel,
                pageType: response.data.pageType,
                documents: response.data.documents.map(d => d.name)
            });

        } else {
            console.error('❌ Erro ao carregar documentos:', response.message);
            notify('Erro ao carregar documentos', 'error');
        }

    } catch (error) {
        console.error('❌ Erro na requisição de documentos:', error);
        notify('Erro de conexão ao carregar documentos', 'error');
    } finally {
        loadingDocuments.value = false;
        isLoadingAfterAuth.value = false;
    }
}

onMounted(async () => {
    // Simulando dados que viriam da API quando o usuário configurar sua página
    pageTitle.value = route.params.pageTitle || 'Portal de Arquivos';
    pageDescription.value = route.params.pageDescription || 'Acesse e baixe seus arquivos com segurança e facilidade.';

    // Verificar se a página requer autenticação baseado no parâmetro 'protected'
    isProtectedPage.value = route.query.protected === 'true';

    if (isProtectedPage.value) {
        // Para páginas protegidas, aguardar autenticação
        // Documentos serão carregados após autenticação bem-sucedida
    } else {
        // Para páginas públicas, carregar documentos imediatamente com loading
        await loadDocuments();
    }
});

async function downloadIndividualDocument(doc) {
    loadingFull({
        enabled: true,
        text: `Baixando ${doc.name}...`,
        subText: 'Por favor, aguarde.'
    });
    try {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simula o download
        notify(`Documento '${doc.name}' baixado com sucesso!`, 'success');
    } catch (error) {
        notify(`Erro ao baixar o documento '${doc.name}'.`, 'error');
    } finally {
        loadingFull({ enabled: false });
    }
}

async function downloadAllDocuments() {
    loadingFull({
        enabled: true,
        text: `Baixando todos os ${documents.value.length} documentos...`,
        subText: 'Isso pode levar alguns instantes.'
    });
    try {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Simula o download de todos
        notify(`Todos os ${documents.value.length} documentos foram baixados com sucesso!`, 'success');
    } catch (error) {
        notify(`Erro ao baixar todos os documentos.`, 'error');
    } finally {
        loadingFull({ enabled: false });
    }
}
</script>

<style scoped lang="scss">
.shareDocument_wrapper {
    min-height: 100vh;
    background-color: var(--greyLight-1, #f8f9fa);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1.5em;
    font-family: 'Inter', sans-serif;

    @media (max-width: 768px) {
        padding: 1em;
    }
}

.shareDocument_container {
    width: 100%;
    max-width: 50em;
}

.shareDocument_card {
    background-color: var(--white, #ffffff);
    border-radius: 1em;
    box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.08);
    border: 1px solid var(--greyLight-2, #e5e7eb);
    overflow: hidden;
}

// Header Section
.shareDocument_header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 2em 2em;
    border-radius: 1em 1em 0 0;
    color: var(--white, #ffffff);
    position: relative;
    overflow: hidden;
    text-align: center;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        z-index: 0;
    }

    @media (max-width: 768px) {
        padding: 1.5em 1.5em;
    }
}

.shareDocument_status-fixed {
    position: absolute;
    top: 1.5em;
    right: 1.5em;
    z-index: 2;

    @media (max-width: 768px) {
        top: 1em;
        right: 1em;
    }
}

.shareDocument_header-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5em;
    position: relative;
    z-index: 1;
    max-width: 40em;
    margin: 0 auto;
    padding-top: 1em;

    @media (max-width: 768px) {
        gap: 1.25em;
        padding-top: 0.5em;
    }
}

.shareDocument_logo {
    display: flex;
    justify-content: center;
}

.shareDocument_logo-image {
    width: 5em;
    height: 5em;
    border-radius: 1em;
    object-fit: contain;
    object-position: center;
    border: 2px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0.5em 1.5em rgba(0, 0, 0, 0.2);
    background-color: rgba(255, 255, 255, 0.1);
    transition: transform 0.3s ease;
    display: block;

    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        width: 4em;
        height: 4em;
    }
}

.shareDocument_content-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
    text-align: center;
}

.shareDocument_title {
    font-size: 1.75em;
    font-weight: 700;
    color: var(--white, #ffffff);
    margin: 0;
    text-shadow: 0 0.125em 0.5em rgba(0, 0, 0, 0.3);
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 1.5em;
    }
}

.shareDocument_description {
    font-size: 0.95em;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
    text-shadow: 0 0.125em 0.25em rgba(0, 0, 0, 0.2);
    max-width: 28em;

    @media (max-width: 768px) {
        font-size: 0.9em;
    }
}

.shareDocument_status-container {
    display: flex;
    align-items: center;
    gap: 0.5em;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    padding: 0.625em 1em;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0.25em 0.75em rgba(0, 0, 0, 0.1);
}

.shareDocument_status-indicator {
    width: 0.625em;
    height: 0.625em;
    background-color: #10b981;
    border-radius: 50%;
    box-shadow: 0 0 0 0.125em rgba(16, 185, 129, 0.4);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    }
    70% {
        box-shadow: 0 0 0 0.625em rgba(16, 185, 129, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
}

.shareDocument_status-badge {
    font-size: 0.8em;
    font-weight: 600;
    color: var(--white, #ffffff);
    text-shadow: 0 0.125em 0.25em rgba(0, 0, 0, 0.2);
    letter-spacing: 0.025em;
}

// Footer Section
.shareDocument_footer {
    background: var(--greyLight-3, #f3f4f6);
    padding: 2em;
    border-radius: 0 0 1em 1em;
    border-top: 1px solid var(--greyLight-2, #e5e7eb);
    text-align: center;

    @media (max-width: 768px) {
        padding: 1.5em;
    }
}

.shareDocument_security {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 40em;
    margin: 0 auto;
}

.shareDocument_security-badges {
    display: flex;
    gap: 2em;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
        gap: 1.5em;
    }

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 1.25em;
        align-items: center;
    }
}

.shareDocument_security-badge {
    display: flex;
    align-items: center;
    gap: 0.5em;
    background: var(--white, #ffffff);
    padding: 0.75em 1em;
    border-radius: 0.75em;
    border: 1px solid var(--greyLight-2, #e5e7eb);
    box-shadow: 0 0.125em 0.5em rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    flex: 1;
    justify-content: center;
    min-width: 0;

    &:hover {
        box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.12);
        transform: translateY(-0.125em);
    }

    @media (max-width: 768px) {
        flex: none;
        min-width: auto;
    }
}

.shareDocument_security-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.02);
    }
}

.shareDocument_security-icon {
    font-size: 1em;
    color: #10b981;
    width: 1em;
    height: 1em;
    display: inline-block;
    flex-shrink: 0;
    transition: color 0.2s ease;

    .shareDocument_security-link:hover & {
        color: #059669;
    }
}

.shareDocument_security-text {
    font-size: 0.875em;
    font-weight: 600;
    color: var(--greyDark-1, #1f2937);
    white-space: nowrap;
}


// Content Section
.shareDocument_content {
    padding: 2em;

    @media (max-width: 768px) {
        padding: 1.5em;
    }
}

.shareDocument_summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5em;
    padding-bottom: 1.5em;
    border-bottom: 1px solid var(--greyLight-2, #e5e7eb);

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1em;
        align-items: stretch;
    }
}

.shareDocument_summary-text {
    font-size: 1em;
    font-weight: 600;
    color: var(--greyDark-1, #1f2937);
}

.shareDocument_documents {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
}

.shareDocument_document-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1em;
    border: 1px solid var(--greyLight-2, #e5e7eb);
    border-radius: 0.75em;
    background-color: var(--white, #ffffff);
    transition: all 0.2s ease;

    &:hover {
        background-color: var(--greyLight-3, #f9fafb);
        border-color: var(--greyLight-1, #d1d5db);
    }
}

.shareDocument_document-info {
    display: flex;
    align-items: center;
    gap: 1em;
    flex: 1;
}

.shareDocument_document-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.5em;
    background-color: var(--greyLight-3, #f3f4f6);
}

.shareDocument_document-iconSvg {
    font-size: 1.25em;
    
    // Cores específicas para cada tipo de arquivo
    &[class*="file-pdf"] {
        color: var(--red-500, #ef4444);
    }
    
    &[class*="file-word"] {
        color: var(--blue-600, #2563eb);
    }
    
    &[class*="file-powerpoint"] {
        color: var(--orange-500, #f97316);
    }
    
    &[class*="file-excel"] {
        color: var(--green-600, #16a34a);
    }
    
    &[class*="file-text"] {
        color: var(--greyDark-2, #6b7280);
    }
}

.shareDocument_document-details {
    display: flex;
    flex-direction: column;
    gap: 0.25em;
}

.shareDocument_document-name {
    font-size: 1em;
    font-weight: 500;
    color: var(--greyDark-1, #1f2937);
}

.shareDocument_document-meta {
    font-size: 0.875em;
    color: var(--greyDark-2, #6b7280);
}

// Responsive adjustments
@media (max-width: 768px) {
    .shareDocument_document-item {
        padding: 0.875em;
    }

    .shareDocument_document-info {
        gap: 0.75em;
    }

    .shareDocument_document-icon {
        width: 2.25em;
        height: 2.25em;
    }

    .shareDocument_document-name {
        font-size: 0.9em;
    }

    .shareDocument_document-meta {
        font-size: 0.8em;
    }
}

// Formulário de Autenticação Integrado
.shareDocument_auth {
    text-align: center;
    max-width: 28em;
    margin: 0 auto;
    padding: 2em 0;
}

.shareDocument_auth-header {
    display: flex;
    align-items: center;
    gap: 1em;
    justify-content: center;
    margin-bottom: 2em;
}

.shareDocument_auth-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.75em;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    font-size: 1.25em;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    flex-shrink: 0;
}

.shareDocument_auth-text {
    text-align: left;
}

.shareDocument_auth-title {
    font-size: 1.5em;
    font-weight: 700;
    color: var(--greyDark-1, #1f2937);
    margin: 0 0 0.25em 0;
    line-height: 1.2;
}

.shareDocument_auth-description {
    font-size: 0.9em;
    color: var(--greyDark-2, #6b7280);
    margin: 0;
    line-height: 1.3;
}

.shareDocument_auth-form {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
    text-align: left;
}

.shareDocument_auth-field {
    position: relative;
}

.shareDocument_auth-error {
    margin: -0.5em 0 1em 0;
}

.shareDocument_auth-actions {
    margin-top: 0.5em;

    .sw-button {
        width: 100%;
    }
}

// Responsividade para autenticação
@media (max-width: 768px) {
    .shareDocument_auth {
        padding: 1.5em 0;
    }

    .shareDocument_auth-header {
        flex-direction: column;
        gap: 0.75em;
        margin-bottom: 1.5em;
    }

    .shareDocument_auth-icon {
        width: 2.25em;
        height: 2.25em;
        font-size: 1.1em;
    }

    .shareDocument_auth-text {
        text-align: center;
    }

    .shareDocument_auth-title {
        font-size: 1.375em;
    }

    .shareDocument_auth-form {
        gap: 1.25em;
    }

    .shareDocument_auth-status {
        margin-bottom: 1.25em;
    }

    .shareDocument_auth-status-badge {
        padding: 0.625em 1em;
        gap: 0.375em;
    }

    .shareDocument_auth-status-icon {
        font-size: 0.875em;
    }

    .shareDocument_auth-status-text {
        font-size: 0.8125em;
    }
}

// Loading State para Documentos
.shareDocument_loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4em 2em;
    text-align: center;
}

// Badge de Status Autenticado
.shareDocument_auth-status {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5em;
}

.shareDocument_auth-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05));
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 9999px;
    padding: 0.75em 1.25em;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    &:hover {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08));
        border-color: rgba(16, 185, 129, 0.3);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
    }
}

.shareDocument_auth-status-icon {
    font-size: 1em;
    color: #10b981;
}

.shareDocument_auth-status-text {
    font-size: 0.875em;
    font-weight: 600;
    color: #047857;
    letter-spacing: 0.025em;
}

// Estado vazio de documentos
.shareDocument_empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4em 2em;
    text-align: center;
}

.shareDocument_empty-content {
    max-width: 24em;
}

.shareDocument_empty-icon {
    font-size: 3em;
    color: var(--greyDark-2, #9ca3af);
    margin-bottom: 1em;
    opacity: 0.6;
}

.shareDocument_empty-title {
    font-size: 1.25em;
    font-weight: 600;
    color: var(--greyDark-1, #374151);
    margin: 0 0 0.5em 0;
}

.shareDocument_empty-description {
    font-size: 0.9em;
    color: var(--greyDark-2, #6b7280);
    line-height: 1.5;
    margin: 0;
}

</style>